import dayjs, { Dayjs, ManipulateType } from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { LOCALE_IMPORTS } from "@companion-core/shared/app/Constants/locales";
dayjs.extend(localizedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

export const API_DATE_FORMAT = "YYYY/MM/DD";

/**
 * Loads the specified locale into the application.
 *
 * @param {string} lang - The language code of the locale to be loaded.
 * @returns {Promise} - A promise that resolves after the locale is loaded.
 */
export const loadLocale = async (lang: string): Promise<void> => {
  // English is the default locale, so we don't need to load it.
  if (lang !== "en") {
    const langToLoad = LOCALE_IMPORTS[lang];
    if (langToLoad) await langToLoad();
  }
  dayjs.locale(lang);
};

/**
 * Retrieves the localized date format.
 *
 * @returns {string} The localized date format.
 */
export const getLocalizedDateFormat = (): string => {
  return dayjs.localeData().longDateFormat("L");
};

/**
 * Retrieves the localized time format.
 *
 * @returns {string} The localized time format.
 */
export const getLocalizedTimeFormat = (): string => {
  return dayjs.localeData().longDateFormat("LT");
};

/**
 * Converts a localized string to a Dayjs object representing date and time.
 *
 * @param {string} date - The localized string representation of date.
 * @returns {Dayjs} - A Dayjs object representing the converted date.
 */
export const localizedStringToDate = (date: string): Dayjs => {
  return dayjs(date, "L");
};

/**
 * Converts a localized string to a Dayjs object representing time.
 *
 * @param {string} time - The localized time string.
 * @returns {Dayjs} The Dayjs object representing the time.
 */
export const localizedStringTotime = (time: string): Dayjs => {
  return dayjs(time, "LT");
};

/**
 * Converts a Dayjs instance to a localized string representation of the date.
 *
 * @param {Dayjs} date - The Dayjs instance to convert.
 * @returns {string} - The localized string representation of the date.
 */
export const dateToLocalizedString = (date: Dayjs): string => {
  return dayjs(date).format("L");
};

/**
 * Converts a Dayjs object to a localized string representation of time.
 *
 * @param {Dayjs} time - The Dayjs object representing the time.
 * @returns {string} - The localized string representation of time.
 */
export const timeToLocalizedString = (time: Dayjs): string => {
  return dayjs(time).format("LT");
};

/**
 * Checks if we have a valid Timezone
 * @param {string} [timezone] - The timezone in string format (e.g., "Europe/Paris").
 * @returns {Boolean} - Returns true if the timezone is found.
 */
export const isValidTimezone = (timezone: string | undefined | null): boolean => {
  if (!timezone) return false; // return false when timezone is null or undefined
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
};

/**
 * This is a workaround because We can't use dayjs.tz (it does not work on ios)
 * It should be fixed in the last versions of react native
 * Link of the issue: https://github.com/iamkun/dayjs/issues/2891
 * Calculates the offset to add (in minutes) from a string timezone
 * @param {string} [timeZone] - The timezone in string format (e.g., "Europe/Paris").
 * @param {Date} [date] - The date (e.g., "Europe/Paris").
 * @returns {number} - Returns the offset to add to the date.
 */
export const getOffsetFromTimezone = (timeZone: string, date: Date = new Date()): number => {
  try {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
      hour: "2-digit",
      minute: "2-digit",
    });
    const formatted = formatter.format(date);
    const match = formatted.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
    if (!match) return 0;
    const sign = match[1] === "-" ? -1 : 1; //
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3] || "0", 10);
    return sign * (hours * 60 + minutes);
  } catch {
    return 0;
  }
};

/**
 * Converts a date and optional time to a payload format using the localized date and time formats.
 *
 * The payload format is based on the provided date and time, and is formatted using the localized date and time formats.
 *
 * @param {string} date - The date in string format (e.g., "2022-01-01").
 * @param {string} [time] - Optional. The time in string format (e.g., "14:30").
 * @param {string} [timezone] - Optional. The timezone in string format (e.g., "Europe/Paris").
 * @returns {string} - The date and time formatted in the payload format.
 */
export const datetimeToPayloadFormat = (date: string, time?: string, timezone?: string) => {
  const dateFormat = getLocalizedDateFormat();
  const timeFormat = getLocalizedTimeFormat();
  const datetime = time ? `${date} ${time}` : date;
  const format = time ? `${dateFormat} ${timeFormat}` : dateFormat;
  const dateWithoutOffset = dayjs.utc(datetime, format);
  const validTimezone: string = isValidTimezone(timezone) ? (timezone as string) : dayjs.tz.guess();
  // We need to pass the date because the timezone offset depends on the date (to handle DST changes)
  const OffsetToAdd = getOffsetFromTimezone(validTimezone, dateWithoutOffset.toDate());
  return dateWithoutOffset.utcOffset(OffsetToAdd, true).format();
};

/**
 * Converts a date to a payload format using YYYY-MM-DD date format.
 **
 * @param {string} date - The date in string format (e.g., "2022/01/01").
 * @returns {string} - The date formatted in the payload format.
 */
export const dateInputToDatePayloadFormat = (date: string) =>
  localizedStringToDate(date).format("YYYY-MM-DD");

/**
 * Converts a date to a HH:mm date format.
 **
 * @param {string} date - The date in string format (e.g., "2025-11-26T00:00:00.000Z").
 * @returns {string} - The datetime formatted in the HH:mm format.
 */
export const formatDateToHHmmFormat = (date: string) => dayjs.utc(date).format("HH:mm");

/**
 * Converts a date to a YYYY-MM-DD date format.
 **
 * @param {string} date - The date in string format (e.g., "2025-11-26T15:00:00.000Z").
 * @returns {boolean} - The datetime formatted in the HH:mm format.
 */
export const formatDateToISOFormat = (date: string) => dayjs(date).format("YYYY-MM-DD");

export const getDate = (date: string) => {
  const number = date.match(/(\d+)/);
  const unit = date.match(/(day|week|month|year)/g);
  if (number && unit) return dayjs().add(+number[0], unit[0] as ManipulateType);
  return date === "now" ? dayjs() : dayjs(date);
};

export const isBetweenDates = (d: Dayjs, minimumDate: string, maximumDate: string) => {
  return !!(
    !d ||
    (minimumDate && d.isBefore(getDate(minimumDate), "day")) ||
    (maximumDate && d.isAfter(getDate(maximumDate), "day"))
  );
};

/**
 * Checks if the current locale's time format is in 12-hour format.
 *
 * @returns {boolean} - `true` if the time format is in 12-hour format, `false` otherwise.
 */
export const is12HourFormat = () => {
  try {
    return /[Aa]/.test(getLocalizedTimeFormat());
  } catch {
    return false;
  }
};

/**
 * Get today date
 *
 * @returns {string} - current date and time
 */
export const dateNow = (): string => dayjs().format();

/**
 *
 * @param {string} date - Input date
 * @param {string} format - Expected format
 * @param {boolean} strict - Uses strict mode. Default true.
 *
 * @returns {boolean} - Returns true if the date described by `date` is a valid date in the format `format`.
 * `strict` to true will also validate delimiters.
 */
export const isDateMatchesFormat = (
  date: string,
  format: string,
  strict: boolean = true
): boolean => {
  return dayjs(date, format, strict).isValid();
};

/**
 * checks if a date is valid.
 **
 * @param {string} date - The date in string format (e.g., "2025-11-26T15:00:00.000Z").
 * @returns {boolean} - Returns true if the date is valid
 */
export const isValidDate = (date: string) => dayjs(date).isValid();
